import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, PlusCircle, Search, Filter, Phone, Mail, Calendar, FileText, FileCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import CrmActivitiesService from '@/api/crmActivities.service';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

interface CRMActivity {
  id?: number;
  publicId: string;
  customerId?: number;
  companyId?: number;
  businessId: number;
  activityType: string;
  title: string;
  description?: string;
  date: string | Date;
  priority?: 'low' | 'medium' | 'high';
  status?: 'planned' | 'completed' | 'canceled';
  assignedTo?: number;
  followUpDate?: string | Date;
  createdBy: number;
  dateCreated?: string | Date;
  dateUpdated?: string | Date;
  customer?: any; // This will contain customer details when populated
  company?: any; // This will contain company details when populated
  customerName?: string;
  assignedToName?: string;
} 

export default function CrmActivities() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activities, setActivities] = useState<CRMActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalActivities, setTotalActivities] = useState(0);
  
  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  const fetchActivities = async () => {
    if (!selectedBusiness) return;
    
    setIsLoading(true);
    try {
      const response = await CrmActivitiesService.getAllActivities(selectedBusiness.id, currentPage);
      if (response.data.result === 'success') {
        setActivities(response.data.crmActivities || []);
        setTotalPages(response.data.totalPages || 1);
        setTotalActivities(response.data.totalActivities || 0);
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to load activities",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occurred while fetching activities",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Search activities
  const searchActivities = async () => {
    if (!selectedBusiness || !searchTerm) return;
    
    setIsLoading(true);
    try {
      const response = await CrmActivitiesService.searchActivities(selectedBusiness.id, searchTerm);
      if (response.data.result === 'success') {
        setActivities(response.data.crmActivities || []);
        setTotalActivities(response.data.crmActivities?.length || 0);
        setTotalPages(1); // Search results are not paginated in this implementation
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to search activities",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Apply filters
  const filterActivities = async () => {
    if (!selectedBusiness) return;
    
    setIsLoading(true);
    try {
      let filteredActivities = [...activities];
      
      // Apply type filter
      if (typeFilter !== 'all') {
        const response = await CrmActivitiesService.getActivitiesByType(selectedBusiness.id, typeFilter);
        if (response.data.result === 'success') {
          filteredActivities = response.data.crmActivities || [];
        }
      } else {
        await fetchActivities();
        return;
      }
      
      // Apply status filter (client-side)
      if (statusFilter !== 'all') {
        filteredActivities = filteredActivities.filter(activity => activity.status === statusFilter);
      }
      
      setActivities(filteredActivities);
      setTotalActivities(filteredActivities.length);
      setTotalPages(1); // Filtered results are not paginated in this implementation
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to filter activities",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Reset filters
  const resetFilters = () => {
    setTypeFilter('all');
    setStatusFilter('all');
    setSearchTerm('');
    fetchActivities();
  };

  useEffect(() => {
    if (selectedBusiness) {
      fetchActivities();
    }
  }, [selectedBusiness, currentPage]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm) {
        searchActivities();
      } else if (typeFilter !== 'all' || statusFilter !== 'all') {
        filterActivities();
      } else {
        fetchActivities();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, typeFilter, statusFilter]);

  // Get icon based on activity type
  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'call':
        return <Phone className="h-4 w-4 text-blue-700" />;
      case 'email':
        return <Mail className="h-4 w-4 text-green-700" />;
      case 'meeting':
        return <Calendar className="h-4 w-4 text-amber-700" />;
      case 'note':
        return <FileText className="h-4 w-4 text-purple-700" />;
      case 'task':
        return <FileCheck className="h-4 w-4 text-slate-700" />;
      default:
        return <FileText className="h-4 w-4 text-primary" />;
    }
  };

  // Get color based on activity type
  const getActivityColorClass = (type: string) => {
    switch (type) {
      case 'call':
        return 'bg-blue-100';
      case 'email':
        return 'bg-green-100';
      case 'meeting':
        return 'bg-amber-100';
      case 'note':
        return 'bg-purple-100';
      case 'task':
        return 'bg-slate-100';
      default:
        return 'bg-primary/10';
    }
  };

  // Get status badge color
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'planned':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'canceled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  // Format activity date
  const formatActivityDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy • h:mm a');
    } catch (e) {
      return dateString;
    }
  };

  const activityTypeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'call', label: 'Calls' },
    { value: 'email', label: 'Emails' },
    { value: 'meeting', label: 'Meetings' },
    { value: 'note', label: 'Notes' },
    { value: 'task', label: 'Tasks' },
  ];

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'planned', label: 'Planned' },
    { value: 'completed', label: 'Completed' },
    { value: 'canceled', label: 'Canceled' },
  ];

  return (
    <div className="container py-8 max-w-7xl mx-auto">
      <div className="flex flex-col space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Customer Activities</h1>
            <p className="text-muted-foreground mt-1">
              Manage and track all customer interactions
            </p>
          </div>
          
          <Button asChild>
            <a href="/crm/activities/new">
              <PlusCircle className="h-4 w-4 mr-2" />
              Add New Activity
            </a>
          </Button>
        </div>
        
        {/* Filters */}
        <Card className="border-border/40">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Search activities..." 
                  className="pl-8"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  {activityTypeOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <div className="col-span-full flex justify-end">
                <Button variant="outline" size="sm" onClick={resetFilters} className="ml-2">
                  Reset Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Activity List */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Activity Log</CardTitle>
            <CardDescription>
              Showing {activities.length} of {totalActivities} activities
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : activities.length > 0 ? (
              <div className="space-y-4">
                {activities.map((activity) => (
                  <div key={activity.publicId} className="p-4 border rounded-lg hover:bg-muted/30 transition-colors">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-2">
                      <div className="flex items-center gap-3">
                        <div className={cn("h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0", getActivityColorClass(activity.activityType))}>
                          {getActivityIcon(activity.activityType)}
                        </div>
                        <div>
                          <h3 className="text-base font-medium">{activity.title}</h3>
                          <p className="text-sm text-muted-foreground">{formatActivityDate(activity.date as string)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className={cn(getStatusBadgeClass(activity.status as string))}>
                          {(activity.status || 'planned').charAt(0).toUpperCase() + (activity.status || 'planned').slice(1)}
                        </Badge>
                        
                        <Button variant="outline" size="sm" asChild>
                          <a href={`/crm/activities/${activity.publicId}`}>View Details</a>
                        </Button>
                      </div>
                    </div>
                    
                    {activity.description && (
                      <p className="text-sm text-muted-foreground mt-2">{activity.description}</p>
                    )}
                    
                    {(activity.customer || activity.priority) && (
                      <div className="mt-3 pt-3 border-t border-border/40 flex flex-wrap gap-3">
                        {activity.customerName && (
                          <Badge variant="secondary" className="bg-secondary/30">
                            Customer: {activity.customerName}
                          </Badge>
                        )}

                        {activity.assignedToName && (
                          <Badge variant="secondary" className="bg-secondary/30">
                            Assigned To: {activity.assignedToName}
                          </Badge>
                        )}
                        
                        {activity.priority && (
                          <Badge variant="outline" className={cn(
                            activity.priority === 'high' ? 'bg-red-50 text-red-700 border-red-200' :
                            activity.priority === 'medium' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                            'bg-blue-50 text-blue-700 border-blue-200'
                          )}>
                            {activity.priority.charAt(0).toUpperCase() + activity.priority.slice(1)} Priority
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <p>No activities found</p>
                {(searchTerm || typeFilter !== 'all' || statusFilter !== 'all') && (
                  <Button variant="outline" size="sm" onClick={resetFilters} className="mt-2">
                    Clear Filters
                  </Button>
                )}
              </div>
            )}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                  disabled={currentPage === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                
                <span className="text-sm text-muted-foreground">
                  Page {currentPage + 1} of {totalPages}
                </span>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                  disabled={currentPage === totalPages - 1}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 